import { useEffect, useRef, useState, type ReactNode } from 'react';

type Props = {
  children: ReactNode;
  placeholder?: ReactNode;
  rootMargin?: string;
  /** Once mounted, stay mounted across scroll-out. Default true. */
  keepMounted?: boolean;
  /** Optional minimum height while waiting, so layout doesn't jump. */
  minHeight?: number | string;
};

export const LazyMountOnVisible: React.FC<Props> = ({
  children,
  placeholder = null,
  rootMargin = '200px',
  keepMounted = true,
  minHeight,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          if (keepMounted) {
            observer.disconnect();
          }
        } else if (!keepMounted) {
          setIsVisible(false);
        }
      },
      { rootMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [rootMargin, keepMounted]);

  return (
    <div ref={ref} style={{ width: '100%', minHeight }}>
      {isVisible ? children : placeholder}
    </div>
  );
};
